/**
 * Minimal i18n architecture skeleton.
 *
 * This is intentionally light — we don't want to migrate every existing
 * string today. Instead we expose:
 *   - SUPPORTED_LOCALES
 *   - getLocale() / setLocale()
 *   - useTranslation() hook returning { t, locale, setLocale, locales }
 *
 * Strings live in /lib/i18n/{en,de}.js and merge by namespace. Anything
 * missing from a locale falls back to English. New components can opt in
 * by calling t("namespace.key") instead of hard-coding strings.
 */
import { useCallback, useEffect, useState } from "react";
import en from "./en";
import de from "./de";

export const SUPPORTED_LOCALES = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

const DICTIONARIES = { en, de };
const STORAGE_KEY = "stockaily_locale";

function getStoredLocale() {
  // English is the default for every new visitor + every session.
  // We respect ONLY an explicit, persisted user choice (via the language
  // switcher). Browser locale auto-detection is intentionally disabled
  // because most non-English browsers should still see English first.
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && DICTIONARIES[raw]) return raw;
  } catch { /* SSR or storage disabled */ }
  return "en";
}

let _listeners = new Set();
let _current = typeof window !== "undefined" ? getStoredLocale() : "en";

export function getLocale() { return _current; }

export function setLocale(code) {
  if (!DICTIONARIES[code] || code === _current) return;
  _current = code;
  try { window.localStorage.setItem(STORAGE_KEY, code); } catch { /* noop */ }
  _listeners.forEach((fn) => fn(code));
}

function lookup(dict, dottedKey) {
  if (!dict || !dottedKey) return undefined;
  const parts = dottedKey.split(".");
  let cur = dict;
  for (const p of parts) {
    if (cur && typeof cur === "object" && p in cur) cur = cur[p];
    else return undefined;
  }
  return typeof cur === "string" ? cur : undefined;
}

export function translate(key, vars, locale) {
  const code = locale || _current;
  const fromLocale = lookup(DICTIONARIES[code], key);
  const fromEn = lookup(DICTIONARIES.en, key);
  // Phase 8.A QA fix — when a key is missing from BOTH the active locale
  // AND English, never display the raw dotted key in the UI. Instead,
  // derive a readable humanized fallback from the last segment of the
  // key (e.g. "portfolio.review.weaknesses" → "Weaknesses",
  // "badge_premium_ai" → "Badge Premium AI"). This keeps the UI safe
  // even when translation files lag the code.
  let template = fromLocale ?? fromEn;
  if (template == null) {
    const last = String(key).split(".").pop() || String(key);
    template = last
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    if (typeof console !== "undefined" && console.warn) {
      console.warn(`[i18n] missing translation for key="${key}" → humanized="${template}"`);
    }
  }
  if (vars && typeof template === "string") {
    template = template.replace(/\{(\w+)\}/g, (_, name) => (vars[name] ?? `{${name}}`));
  }
  return template;
}

export function useTranslation() {
  const [code, setCode] = useState(_current);
  useEffect(() => {
    const fn = (newCode) => setCode(newCode);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);
  const t = useCallback((key, vars) => translate(key, vars, code), [code]);
  return { t, locale: code, setLocale, locales: SUPPORTED_LOCALES };
}
